import { useState, useEffect } from 'react';
import { getDatabase, isDatabaseReady } from '../index';

/**
 * Hook to access the current user's database
 * Returns null if database is not initialized (user not logged in)
 * 
 * @returns {{ db: Dexie|null, isReady: boolean }}
 */
export function useDatabase() {
  const [db, setDb] = useState(getDatabase());
  // isReady must never be true while db is null — callers rely on that
  // invariant to skip null checks on db once isReady is true.
  const [isReady, setIsReady] = useState(isDatabaseReady() && !!getDatabase());

  useEffect(() => {
    // Check periodically if database becomes available
    // This handles the case where the hook is used before auth completes
    const checkDb = () => {
      const currentDb = getDatabase();
      const ready = isDatabaseReady() && !!currentDb;

      if (currentDb !== db) {
        setDb(currentDb);
      }
      if (ready !== isReady) {
        setIsReady(ready);
      }
    };

    // Initial check
    checkDb();

    // Set up interval to check for database availability
    const interval = setInterval(checkDb, 100);

    // Clean up after a reasonable time (10 seconds)
    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 10000);

    // The polling above only covers startup races; a database initialized
    // much later (init retried after a server outage) announces itself.
    window.addEventListener('jurybox:db-ready', checkDb);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
      window.removeEventListener('jurybox:db-ready', checkDb);
    };
  }, [db, isReady]);

  return { db, isReady };
}

/**
 * Hook that requires database to be ready
 * Throws an error if database is not initialized
 * Use this in components that should only render when logged in
 * 
 * @returns {Dexie}
 */
export function useRequiredDatabase() {
  const { db, isReady } = useDatabase();
  
  if (!isReady || !db) {
    throw new Error('Database not ready. Ensure user is logged in before using this hook.');
  }
  
  return db;
}

export default useDatabase;
